import { Pipe, PipeTransform, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { PermissionService } from './permission.service';
import { PermissionId } from './permission.types';

/**
 * Async pipe-friendly permission check for `[disabled]`, `[matTooltip]` and
 * other expression bindings where a structural directive is awkward.
 *
 * ```html
 * <button
 *   [disabled]="!('transaction.broadcast' | hasPermission | async)"
 *   [matTooltip]="('transaction.broadcast' | hasPermission | async)
 *     ? '' : ('permissions.notAllowed' | translate)"
 * >Broadcast</button>
 * ```
 *
 * The pipe is `pure: true` (Angular default) — Angular memoises by input
 * reference. Pass a stable string literal or component field, not a freshly
 * constructed array each change-detection cycle.
 */
@Pipe({
  name: 'hasPermission',
  standalone: true,
})
export class HasPermissionPipe implements PipeTransform {
  private readonly permissions = inject(PermissionService);

  transform(
    permissionId: PermissionId | null | undefined,
    mode: 'single' | 'any' | 'all' = 'single'
  ): Observable<boolean> {
    if (!permissionId) {
      return this.permissions.can$('');
    }
    if (mode === 'all') {
      return this.permissions.canAll$(
        Array.isArray(permissionId) ? permissionId : [permissionId]
      );
    }
    if (mode === 'any') {
      return this.permissions.canAny$(
        Array.isArray(permissionId) ? permissionId : [permissionId]
      );
    }
    return this.permissions.can$(permissionId);
  }
}
